import React, { useEffect, useMemo, useState } from 'react';

import UserSearchSelect from '../../components/UserSearchSelect.jsx';
import { TASK_STATUS, TASK_TEAM, TASK_URGENCY, isIndividualUser } from '../../config';
import { assigneesOrSelf } from '../../lib/assignees';
import { useAuth } from '../../lib/auth.jsx';

/**
 * Reusable Task form, used by both Create and Edit pages.
 *
 * Why we keep this in its own file: the underlying Django form
 * (`task_management.forms.TaskForm`) maps to ~10 fields — repeating that
 * markup in two pages would be wasteful.
 */
// Helper: today's date as a YYYY-MM-DD string for ``<input type="date">``.
// Defaulting to today (instead of an empty string) means brand-new
// task forms always come pre-filled with a sensible date — the user
// can still pick a different one, but they don't have to start from
// scratch every time.
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function TaskForm({ initial = {}, onSubmit, submitting }) {
  const { user: currentUser } = useAuth();
  const solo = isIndividualUser(currentUser);

  // Assignment is mandatory.  Create (and edit of an unassigned row)
  // defaults the picker to the logged-in user ("Self").  An explicit
  // non-empty ``initial.users`` is kept so a manager can still delegate.
  const defaultUsers = assigneesOrSelf(initial.users, currentUser?.id);

  const [form, setForm] = useState({
    title: '',
    description: '',
    start_date: todayIso(),
    end_date: todayIso(),
    estimated_hours: '',
    urgency: 'default',
    team: 'individual',
    status: 'assigned',
    ...initial,
    users: defaultUsers,
  });

  const knownUsers = useMemo(() => {
    const rows = [];
    if (currentUser) rows.push(currentUser);
    if (Array.isArray(initial.assignees)) {
      for (const person of initial.assignees) rows.push(person);
    }
    return rows;
  }, [currentUser, initial.assignees]);

  // If the auth context resolves *after* the form mounted (e.g. /me/ was
  // still in-flight), back-fill Self — but only when nobody is selected.
  useEffect(() => {
    if (currentUser?.id == null) return;
    setForm((f) => (
      f.users && f.users.length > 0 ? f : { ...f, users: [currentUser.id] }
    ));
  }, [currentUser?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      estimated_hours: form.estimated_hours === '' ? null : Number(form.estimated_hours),
      users: solo
        ? (currentUser?.id != null ? [currentUser.id] : assigneesOrSelf(form.users, currentUser?.id))
        : assigneesOrSelf(form.users, currentUser?.id),
      team: solo ? 'individual' : form.team,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="ty-card">
      <div className="ty-field">
        <label>Title *</label>
        <input
          className="ty-input"
          required
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
        />
      </div>

      <div className="ty-field">
        <label>Description</label>
        <textarea
          className="ty-textarea"
          value={form.description || ''}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      <div className="ty-grid-2">
        <div className="ty-field">
          <label>Start date</label>
          <input
            className="ty-input"
            type="date"
            value={form.start_date || ''}
            onChange={(e) => set('start_date', e.target.value)}
          />
        </div>
        <div className="ty-field">
          <label>End date</label>
          <input
            className="ty-input"
            type="date"
            value={form.end_date || ''}
            onChange={(e) => set('end_date', e.target.value)}
          />
        </div>

        <div className="ty-field">
          <label>Estimated hours</label>
          <input
            className="ty-input"
            type="number"
            min="0"
            value={form.estimated_hours ?? ''}
            onChange={(e) => set('estimated_hours', e.target.value)}
          />
        </div>
        <div className="ty-field">
          <label>Urgency</label>
          <select
            className="ty-select"
            value={form.urgency}
            onChange={(e) => set('urgency', e.target.value)}
          >
            {TASK_URGENCY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {!solo ? (
        <div className="ty-field">
          <label>Team</label>
          <select
            className="ty-select"
            value={form.team}
            onChange={(e) => set('team', e.target.value)}
          >
            {TASK_TEAM.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        ) : null}
        <div className="ty-field">
          <label>Status</label>
          <select
            className="ty-select"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
          >
            {TASK_STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="ty-field">
        <label htmlFor="task-assign-users">Assign users *</label>
        {solo ? (
          <>
            <input className="ty-input" value="Assigned to you" disabled readOnly />
            <small className="ty-muted">
              Personal accounts keep every task assigned to you.
            </small>
          </>
        ) : (
          <>
            <UserSearchSelect
              id="task-assign-users"
              value={form.users || []}
              onChange={(ids) => set('users', assigneesOrSelf(ids, currentUser?.id))}
              knownUsers={knownUsers}
              selfId={currentUser?.id}
            />
            <small className="ty-muted">
              Defaults to Self. Search by name, mobile or email to add people.
              Three recent assignees appear as suggestions when you open the field.
            </small>
          </>
        )}
      </div>

      <div className="ty-row ty-row--end">
        <button className="ty-btn" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
